import { Controller } from '@hotwired/stimulus';
import {
  generateRsaKeyPair,
  wrapPrivateKeyWithPassword,
  hybridEncryptOrgKey,
  b64encode,
} from '../vault_crypto';

export default class extends Controller {
  static targets = [
    'recoveryCode',
    'passwordField',
    'confirmationField',
    'passwordError',
    'publicKey',
    'wrappedPrivateKey',
    'salt',
    'iv',
    'encryptedOrgKey',
  ];
  static values = { cryptoOptions: Object, minLength: Number, t: Object };

  async bootstrap(event) {
    event.preventDefault();

    const recoveryCode = this.recoveryCodeTarget.value;
    const password = this.passwordFieldTarget.value;
    const confirm = this.confirmationFieldTarget.value;
    const minLen = this.minLengthValue || 12;

    if (!recoveryCode) {
      alert(this.tValue.recovery_code_required);
      return;
    }
    if (password.length < minLen) {
      alert(this.tValue.password_too_short);
      return;
    }
    if (password !== confirm) {
      alert(this.tValue.passwords_do_not_match);
      return;
    }
    // the recovery code and the vault password must be different secrets
    if (password === recoveryCode) {
      alert(this.tValue.password_equals_recovery);
      return;
    }

    try {
      // 1. Unwrap the org private key using the recovery code
      const opts = this.cryptoOptionsValue;
      const decryptor = new Decrypt({
        wrapped_key: opts.wrapped_key,
        salt: opts.salt,
        iv: opts.iv,
        enc_shared_keys: [],
      });
      await decryptor.setPasswordUnwrapKey(recoveryCode);
      const orgPrivBytes = await crypto.subtle.exportKey('pkcs8', decryptor.privateKey);

      // 2. Generate user's personal RSA keypair and password-wrap the private key
      const userKeyPair = await generateRsaKeyPair();
      const { wrappedKey, salt, iv } = await wrapPrivateKeyWithPassword(
        password,
        userKeyPair.privateKey
      );

      // 3. Hybrid-encrypt org private key to user's public key
      const exportedPub = await crypto.subtle.exportKey('spki', userKeyPair.publicKey);
      const encOrgPrivateKey = `0:${await hybridEncryptOrgKey(orgPrivBytes, userKeyPair.publicKey)}`;

      this.publicKeyTarget.value = b64encode(exportedPub);
      this.wrappedPrivateKeyTarget.value = b64encode(wrappedKey);
      this.saltTarget.value = b64encode(salt);
      this.ivTarget.value = b64encode(iv);
      this.encryptedOrgKeyTarget.value = encOrgPrivateKey;

      this.recoveryCodeTarget.disabled = true;
      this.passwordFieldTarget.disabled = true;
      this.confirmationFieldTarget.disabled = true;
      this.element.submit();
    } catch (e) {
      console.error('Recovery bootstrap failed:', e);
      alert(this.tValue.invalid_recovery_code);
    }
  }
}
